import React, {useContext, useRef, useState} from "react";
import {Button, message, Table, Modal, Switch, Tag} from "antd";
import dayjs from "dayjs";
import TablePage from "../../pages/tablePage/TablePage";
import {AuthContext} from "../../shared/contexts/authContext";
import {getItemName} from "../../shared/utils/basic";
import StoreManagerEditModal from "./StoreManagerEditModal";
import {ENV, REFRESH_TYPE_FUNCTION} from "../../shared/utils/enum";
import {useItemKrTextData, useShopPackage, useShopPackageGroup} from "../probability/useItemData";
import toast from "react-hot-toast";
import {internalApi} from "../../common/axios";
import {useReloadTime} from "../serverInfo/useServerInfo";
import ErrorPage from "../../shared/components/ErrorPage";
import {ShopPackageEntity} from "./ShopPackageEntity";
import {SHOP_PACKAGE_CSV_COLUMNS} from "./shopPackageCsvColumns";

const StoreManagerMain = () => {
    const {currentUser} = useContext(AuthContext);

    const [rowData, setRowData] = useState({});
    const [csvData, setCsvData] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectRowSeqList, setSelectRowSeqList] = useState([]);
    const [isChanged, setIsChanged] = useState(false);

    const refreshRef = useRef(null);

    const {data: krItemTextData, isLoading: isTextLoading, error: textError} = useItemKrTextData();
    const {data: shopPackage, isLoading: isPackageLoading, error: packageError} = useShopPackage();
    const {data: shopPackageGroup, isLoading: isGroupLoading, error: groupError} = useShopPackageGroup();
    const {data: reloadTimeData, isLoading: isReloadTimeData, error: reloadTimeDataError, refetch: reloadTimeDataRefetch} = useReloadTime();

    // Shop_Package.csv(shopPackage) 와 대조해 업로드 전에 걸러낸다. 서버에서도 동일하게 검사한다.
    const checkCsvRow = (entity) => {
        const errors = [];
        const table = shopPackage?.[currentUser.target] || {};
        const setting = table[entity.index];
        if (!setting) {
            errors.push(`index ${entity.index} 는 Shop_Package.csv 에 없는 패키지입니다.`);
            return {errors, fixes: []};
        }
        const groups = Object.values(shopPackageGroup?.[currentUser.target] || {});
        if (!groups.some(group => group.groupIdx === setting.groupIdx)) {
            errors.push(`groupIdx ${setting.groupIdx} 가 Shop_Package_Group.csv 에 없습니다.`);
        }

        const fixes = [];
        if (![0, 1].includes(entity.onOff)) {
            fixes.push(`onOff: ${entity.onOff} -> 0 (0 또는 1)`);
            entity.onOff = 0;
        }
        [['packageTime', 0], ['saleType', 0], ['maxBuyCount', 0], ['coolTime', 0]].forEach(([field, fallback]) => {
            if (Number.isFinite(entity[field]) && entity[field] >= 0) return;
            fixes.push(`${field}: ${entity[field]} -> ${fallback} (0 이상의 숫자)`);
            entity[field] = fallback;
        });
        return {errors, fixes};
    };

    const columns = [
        Table.EXPAND_COLUMN,
        {
            title: '시퀀스',
            dataIndex: 'seq',
            key: 'seq',
            search: true,
            sorter: (a, b) => a.seq - b.seq,
            width: 100,
        },
        {
            title: '패키지 인덱스',
            dataIndex: 'index',
            key: 'index',
            search: true,
            sorter: (a, b) => a.index - b.index,

        },
        {
            title: '패키지 이름',
            dataIndex: 'packageName',
            key: 'packageName',
            search: true
        },
        {
            title: 'On/Off',
            dataIndex: 'onOff',
            render: (_, record) => (
                <Switch
                    checked={record.onOff === 1}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={checked => handleSwitchChange(checked, record)}
                    onClick={(_, e) => e.stopPropagation()}
                />
            ),
            filters: [
                {
                    text: 'On',
                    value: 1,
                },
                {
                    text: 'Off',
                    value: 0,
                },
            ],
        },
        {
            title: '시작 시간',
            dataIndex: 'begin',
            sorter: true,
            key: 'begin',
            render: (begin) => dayjs.unix(begin).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '종료 시간',
            dataIndex: 'end',
            sorter: true,
            key: 'end',
            render: (end) => dayjs.unix(end).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '패키지 별 운영기간(h)',
            dataIndex: 'packageTime',
            key: 'packageTime',
        },
        {
            title: '할인 타입',
            dataIndex: 'saleType',
            key: 'saleType',
        },
        {
            title: '판매 타입',
            dataIndex: 'maxBuyCount',
            key: 'maxBuyCount',
            render: (_, {maxBuyCount, coolTime}) => maxBuyCount > 0
                ? <Tag color="blue">기간 내 {maxBuyCount}회 / {coolTime}분</Tag>
                : <Tag>기간 내</Tag>,
        },
        {
            title: '메모',
            dataIndex: 'memo',
            key: 'memo',
            width: '200px',
        },
        {
            title: '최소 적용 버전',
            dataIndex: 'minVersion',
            key: 'minVersion',
            render: (_, {index}) => (
                <Tag>{shopPackage[currentUser.target][index]?.openVersion || '테이블에 값을 넣어주세요.'}</Tag>
            )
        },
    ];
    const expandColumns = [
        {
            title: '인덱스',
            dataIndex: 'index',
            key: 'index',
        },
        {
            title: '상품이름',
            dataIndex: 'nameText',
            key: 'nameText',
        },
        {
            title: '인앱 인덱스',
            dataIndex: 'inAppIdx',
            key: 'inAppIdx',
        },
        {
            title: '다이아 가격',
            dataIndex: 'diaBuyValue',
            key: 'diaBuyValue',
        },
        {
            title: '보상 아이템',
            dataIndex: 'rewards',
            key: 'rewards',
            render: (rewards) => rewards.map((reward, idx) => (
                <div key={`${reward.id}-${idx}`}>
                    {getItemName(krItemTextData[currentUser.target], reward.id, reward.type)} x{reward.quantity}
                </div>
            )),
        },
    ];

    const expandedRowRender = (record) => {
        try {
            return (
                <Table columns={expandColumns}
                       dataSource={Object.values(shopPackageGroup[currentUser.target])
                           .filter((group) => group.groupIdx === shopPackage[currentUser.target][record.index]?.groupIdx)
                           .map((group, index) => ({
                               ...group,
                               key: group.groupIdx + index,
                           }))}
                       pagination={false}
                />
            )
        } catch (e) {
            console.error(e);
            message.error(`패키지 그룹 데이터를 불러오는 중 에러가 발생했습니다.\n CSV 파일 및 서버 데이터를 확인해주세요.`);
        }
    };

    const handleCopyLiveData = async () => {
        if (currentUser.target === ENV.PRODUCTION) {
            toast.error('운영 환경에서는 라이브 데이터 복사 기능을 사용할 수 없습니다.');
            return;
        }
        const result = await internalApi.put('/store_manager');
        if (result) {
            toast.success('라이브 데이터 복사가 완료되었습니다.');
            if (refreshRef.current) {
                refreshRef.current(REFRESH_TYPE_FUNCTION.GAME_DATA);
            }
        }
    }

    const showCopyConfirm = () => {
        Modal.confirm({
            title: 'Live 데이터 복사',
            content: '정말로 Live 데이터를 복사하시겠습니까?',
            okText: '확인',
            cancelText: '취소',
            onOk: handleCopyLiveData,
        });
    };

    // 선택 복사 함수
    const handleCopySelected = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('복사할 항목을 선택하세요.');
            return;
        }
        Modal.confirm({
            title: '선택 데이터 복사하기',
            content: '정말로 선택한 항목을 복사하시겠습니까? 복사 시 시작일과 종료일에 1일씩 더해집니다.',
            okText: '확인',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await internalApi.post('/store_manager/bulk_copy', {seqList: selectRowSeqList});
                    message.success('복사가 완료되었습니다.');
                    setSelectedRowKeys([]);
                    setSelectRowSeqList([]);
                    if (refreshRef.current) {
                        refreshRef.current(REFRESH_TYPE_FUNCTION.GAME_DATA);
                    }
                } catch (e) {
                    message.error('복사 중 오류가 발생했습니다.');
                }
            }
        });
    };

    // 선택 삭제 함수
    const handleDeleteSelected = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('삭제할 항목을 선택하세요.');
            return;
        }
        Modal.confirm({
            title: '선택 데이터 삭제하기',
            content: '정말로 선택한 항목을 삭제하시겠습니까?',
            okText: '확인',
            cancelText: '취소',
            onOk: async () => {
                try {
                    // 실제 삭제 API 호출 (엔드포인트는 서버에 맞게 조정)
                    await internalApi.post('/store_manager/bulk_delete', {seqList: selectRowSeqList});
                    message.success('삭제가 완료되었습니다.');
                    setSelectedRowKeys([]);
                    setSelectRowSeqList([]);
                    if (refreshRef.current) {
                        refreshRef.current(REFRESH_TYPE_FUNCTION.GAME_DATA);
                    }
                } catch (e) {
                    message.error('삭제 중 오류가 발생했습니다.');
                }
            }
        });
    };

    // On/Off 스위치 토글 핸들러
    const handleSwitchChange = async (checked, record) => {
        try {
            if (record.end < dayjs().unix()) {
                toast.error('종료된 상점 아이템은 On/Off 상태를 변경할 수 없습니다.');
                return;
            }

            await internalApi.post('/store_manager/onoff', {
                seq: record.seq,
                onOff: checked ? 1 : 0
            });
            toast.success('On/Off 상태가 변경되었습니다.');
            setIsChanged(record.seq + checked);
        } catch (e) {
            toast.error('On/Off 변경 실패');
        }
    };

    // rowSelection 옵션
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
            setSelectRowSeqList(selectedRows.map(row => row.seq));
        }
    };

    if (isTextLoading || isPackageLoading || isGroupLoading || isReloadTimeData) {
        return <div>데이터 로딩 중...</div>;
    }

    const firstError = textError || packageError || groupError || reloadTimeDataError;
    if (firstError) return <ErrorPage error={firstError} />;

    const props = {
        columns,
        rowData,
        setRowData,
        refreshRef,
        isChanged,
        modal: StoreManagerEditModal,
        url: '/store_manager',
        csvUpload: 'store_manager/products/csv',
        csvCompareUrl: 'store_manager?filters={}',
        csvIdentityKey: 'seq',
        csvDeleteMissing: false,
        entityModel: ShopPackageEntity,
        csvColumns: SHOP_PACKAGE_CSV_COLUMNS,
        csvTemplateName: 'shop_package_form.csv',
        csvCheckRow: checkCsvRow,
        csvGuide: [
            '인코딩 : UTF-8(BOM 포함) / 구분자 : 쉼표(,)',
            'Excel 저장 시 [다른 이름으로 저장 > CSV UTF-8(쉼표로 분리)(*.csv)]를 선택해야 한글이 깨지지 않습니다.',
            '날짜 : YYYY-MM-DD HH:mm:ss 또는 unix(초) 둘 다 사용 가능합니다.',
            'seq : 비워두면 신규 등록, 값이 있으면 해당 행 수정입니다.',
            'index 는 Shop_Package.csv 에 등록된 패키지 인덱스여야 하며, 상품 그룹(Shop_Package_Group.csv)이 없으면 저장되지 않습니다.',
            '개인화 패키지는 [개인화 관리] 에서 등록합니다. 이 양식으로는 수정할 수 없습니다.',
            '[CSV 양식 다운로드] 로 빈 양식을 받아 작성하세요. 2번째 # 행은 설명이며 업로드 시 무시됩니다.',
        ],
        tblData: csvData,
        setTblData: setCsvData,
        expandedRowRender,
        refreshTypeFunc: REFRESH_TYPE_FUNCTION.GAME_DATA,
        reloadTime: reloadTimeData?.game_data_sync_id,
        reloadTimeRefetch: reloadTimeDataRefetch
    }

    return (
        <div>
            <div style={{marginBottom: 16, display: 'flex', gap: 8}}>
                {currentUser.target !== ENV.PRODUCTION && (
                    <Button onClick={showCopyConfirm}>
                        Live 데이터 복사하기
                    </Button>
                )}
                <Button
                    onClick={handleCopySelected}
                    disabled={selectedRowKeys.length === 0}
                >
                    선택 데이터 복사하기
                </Button>
                <Button
                    danger
                    onClick={handleDeleteSelected}
                    disabled={selectedRowKeys.length === 0}
                >
                    선택 데이터 삭제하기
                </Button>
            </div>
            <TablePage
                {...props}
                rowSelection={rowSelection}
            />
        </div>
    );
}

export default StoreManagerMain;